import { useTranslation } from '../../i18n'
import { COLORS } from '../../lib/theme'
import { useTeamRoleLabel } from '../../lib/roles'
import { PlanHuntAvatar } from './PlanHuntAvatar'
import { orderPlanHuntMembers } from './orderMembers'
import type { PlanHuntMember } from './types'

export interface PlanHuntLeadersStepProps {
  currentUserId: string
  members: PlanHuntMember[]
  leaderIds: string[]
  onChange: (next: string[]) => void
}

export function PlanHuntLeadersStep({
  currentUserId,
  members,
  leaderIds,
  onChange,
}: PlanHuntLeadersStepProps) {
  const { t } = useTranslation('planHunt')
  const roleLabel = useTeamRoleLabel()
  const selected = new Set(leaderIds)
  // A hunt keeps a leader: whoever is the only one cannot be unticked, as in
  // the mobile app. Only your own row says to pick another leader first.
  const soleLeaderId = leaderIds.length === 1 ? leaderIds[0] : null
  // Creator (current user) first, everyone else alphabetical by first name.
  const ordered = orderPlanHuntMembers(members, currentUserId)

  const toggle = (id: string) => {
    if (id === soleLeaderId) return
    const next = new Set(selected)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    onChange([...next])
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {ordered.map((m) => {
        const active = selected.has(m.id)
        const isMe = m.id === currentUserId
        const locked = m.id === soleLeaderId
        const showPickHint = locked && isMe
        return (
          <label
            key={m.id}
            title={showPickHint ? t('leaders.pickAnotherLeader') : undefined}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '10px 12px',
              borderRadius: 8,
              border: `1.5px solid ${active ? COLORS.primary : COLORS.surfaceContainerHighest}`,
              background: active ? COLORS.primaryContainer : COLORS.surfaceContainerLowest,
              cursor: locked ? 'not-allowed' : 'pointer',
            }}
          >
            <PlanHuntAvatar member={m} size={36} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6 }}>
                {m.name}
                {isMe && <span style={{ fontSize: 11, fontWeight: 500, color: COLORS.outline }}>{t('leaders.you')}</span>}
                {locked && (
                  <span
                    data-testid={`sole-leader-${m.id}`}
                    className="wh-chip"
                    style={{
                      fontSize: 9,
                      padding: '1px 5px',
                      background: COLORS.tertiaryContainer,
                      // Pair the container with its matching on-container text: both
                      // flip together per theme. onTertiaryFixedVariant does NOT flip,
                      // so it went dark-on-dark (invisible) in dark mode.
                      color: COLORS.onTertiaryContainer,
                      borderColor: 'transparent',
                      letterSpacing: 0.3,
                    }}
                  >
                    {t('leaders.soleLeader')}
                  </span>
                )}
              </div>
              {m.role && (
                <div style={{ fontSize: 11, color: COLORS.outline }}>
                  {showPickHint ? t('leaders.pickAnotherLeader') : roleLabel(m.role)}
                </div>
              )}
            </div>
            <input
              type="checkbox"
              checked={active}
              disabled={locked}
              onChange={() => toggle(m.id)}
              aria-label={t('leaders.leaderAria', { name: m.name })}
              className="wh-checkbox"
            />
          </label>
        )
      })}
    </div>
  )
}
